import { add, number } from "mathjs";
import { create } from "zustand";
import type { ItemListId } from "../../tracking/create-ecommerce-event";
import type { PurchaseCatalogProduct as ApiPurchaseCatalogProduct } from "../api/vouchers-distribution.types";
import type { BloodTest } from "../models/blood-test";
import type { BloodTestPackage } from "../models/blood-test-package";
import type { CartProduct } from "../models/cart-product";
import { addToCartEvent, removeFromCartEvent } from "../tracking/events";
import { isSamePurchaseCatalogLine } from "../utils/purchase-catalog-line";

type PurchaseCatalogProduct = ApiPurchaseCatalogProduct & {
  item_list_id?: ItemListId;
  index?: number;
};

export type AddPurchaseCatalogProductResult =
  | { status: "added" }
  | { status: "quantity_updated" }
  | { status: "blocked_test_in_package" }
  | { status: "blocked_products_limit" }
  | { status: "package_added_with_removals"; removedSinglesQuantity: number };

type CartState = {
  products: CartProduct[];
  productsLimit?: number;
  upsellAvailable: boolean;
  isCreatedFromRecommendation: boolean;
  setIsCreatedFromRecommendation: (isCreatedFromRecommendation: boolean) => void;
  addProduct: (product: BloodTestPackage | BloodTest, itemListId?: ItemListId, index?: number) => void;
  addPurchaseCatalogProduct: (product: PurchaseCatalogProduct) => AddPurchaseCatalogProductResult;
  getTotalPrice: () => number;
  getTotalProductsCount: () => number;
  removeProduct: (product: BloodTestPackage) => void;
  clearCart: () => void;
  resetCart: () => void;
  setUpsellAvailable: (available: boolean) => void;
  setProductsLimit: (productsLimit?: number) => void;
  quantityModificationEnabled: boolean;
  setQuantityModificationEnabled: (enabled: boolean) => void;
  updateProductQuantity: (product: Pick<CartProduct, "uuid" | "listItemId">, quantity: number) => void;
};

const initialCartData = {
  products: [] as CartProduct[],
  productsLimit: undefined as number | undefined,
  upsellAvailable: false,
  isCreatedFromRecommendation: false,
  quantityModificationEnabled: false,
};

export const useCartState = create<CartState>((set, get) => ({
  ...initialCartData,
  setIsCreatedFromRecommendation: (isCreatedFromRecommendation) => set({ isCreatedFromRecommendation }),
  setQuantityModificationEnabled: (enabled) => set({ quantityModificationEnabled: enabled }),
  setUpsellAvailable: (available) => set({ upsellAvailable: available }),
  setProductsLimit: (productsLimit) => set({ productsLimit }),
  clearCart: () => set({ products: [] }),
  resetCart: () => set({ ...initialCartData }),
  addProduct: (voucher, itemListId, index) =>
    set((state) => {
      const { products, productsLimit } = state;

      if (productsLimit !== undefined) {
        const totalQuantity = products.reduce((acc, p) => acc + p.quantity, 0);
        if (totalQuantity >= productsLimit) return state;
      }

      const existingProduct = products.find((p) => p.uuid === voucher.uuid);

      if (existingProduct) {
        const updatedProducts = products.map((p) => (p.uuid === voucher.uuid ? { ...p, quantity: p.quantity + 1 } : p));

        addToCartEvent(
          [
            {
              ...existingProduct,
              quantity: 1,
              item_list_id: itemListId,
              index,
              isUpsell: "isUpsell" in voucher ? voucher.isUpsell : undefined,
            },
          ],
          { value: Number(existingProduct.grossPrice) },
        );

        return { products: updatedProducts };
      }

      const isPackage = "bloodTests" in voucher; // BloodTestPackage has bloodTests array

      const newProduct: CartProduct = {
        ...voucher,
        productType: isPackage ? "blood-test-package" : "blood-test",
        bloodTests: isPackage ? voucher.bloodTests.map((bloodTest) => bloodTest.id) : [voucher.id],
        quantity: 1,
        item_list_id: itemListId,
        index,
        isUpsell: "isUpsell" in voucher ? voucher.isUpsell : undefined,
      };

      addToCartEvent([newProduct], {
        value: Number(newProduct.grossPrice),
      });

      return {
        products: [...products, newProduct],
      };
    }),
  addPurchaseCatalogProduct: (voucher) => {
    const outcome: { value: AddPurchaseCatalogProductResult } = {
      value: { status: "added" },
    };

    set((state) => {
      const { products, productsLimit } = state;

      const productType = voucher.productType;

      let nextProducts = products;
      let removedSinglesQuantity = 0;
      let singlesRemovedForAnalytics: CartProduct[] = [];

      if (productType === "blood-test-package") {
        const packageBloodTestIds = new Set(voucher.bloodTests.map((bloodTest) => bloodTest.id));
        singlesRemovedForAnalytics = products.filter(
          (p) =>
            p.productType === "blood-test" && p.bloodTests[0] !== undefined && packageBloodTestIds.has(p.bloodTests[0]),
        );
        removedSinglesQuantity = singlesRemovedForAnalytics.reduce((acc, p) => acc + p.quantity, 0);
        nextProducts = products.filter((p) => {
          if (p.productType === "blood-test") {
            const singleId = p.bloodTests[0];
            return singleId === undefined || !packageBloodTestIds.has(singleId);
          }
          return true;
        });
      } else {
        const bloodTestId = voucher.id;
        const blocked = products.some(
          (p) => p.productType === "blood-test-package" && p.bloodTests.includes(bloodTestId),
        );
        if (blocked) {
          outcome.value = { status: "blocked_test_in_package" };
          return state;
        }
      }

      const existingProduct = nextProducts.find((p) => isSamePurchaseCatalogLine(p, voucher));

      if (productsLimit !== undefined) {
        const baseTotal = nextProducts.reduce((acc, p) => acc + p.quantity, 0);
        const projectedTotal = baseTotal + 1;
        if (projectedTotal > productsLimit) {
          outcome.value = { status: "blocked_products_limit" };
          return state;
        }
      }

      if (singlesRemovedForAnalytics.length > 0) {
        removeFromCartEvent(singlesRemovedForAnalytics);
      }

      if (existingProduct) {
        const updatedProducts = nextProducts.map((p) =>
          isSamePurchaseCatalogLine(p, voucher) ? { ...p, quantity: p.quantity + 1 } : p,
        );

        addToCartEvent(
          [
            {
              ...existingProduct,
              quantity: 1,
              item_list_id: voucher.item_list_id,
              index: voucher.index,
            },
          ],
          {
            value: Number(existingProduct.grossPrice),
          },
        );

        outcome.value = { status: "quantity_updated" };
        return { products: updatedProducts };
      }

      const newProduct: CartProduct = {
        ...voucher,
        productType,
        bloodTests:
          productType === "blood-test-package" ? voucher.bloodTests.map((bloodTest) => bloodTest.id) : [voucher.id],
        quantity: 1,
      };

      addToCartEvent([newProduct], {
        value: Number(newProduct.grossPrice),
      });

      outcome.value =
        productType === "blood-test-package" && removedSinglesQuantity > 0
          ? { status: "package_added_with_removals", removedSinglesQuantity }
          : { status: "added" };

      return {
        products: [...nextProducts, newProduct],
      };
    });

    return outcome.value;
  },
  getTotalPrice: () =>
    get().products.reduce((acc, product) => add(acc, number(product.grossPrice) * number(product.quantity)), 0),
  getTotalProductsCount: () => get().products.reduce((acc, product) => add(acc, number(product.quantity)), 0),
  removeProduct: (voucher) =>
    set((state) => {
      const { products } = state;
      const productToRemove = products.find((p) => isSamePurchaseCatalogLine(p, voucher));

      if (productToRemove) {
        removeFromCartEvent([{ ...productToRemove, isUpsell: "isUpsell" in voucher ? voucher.isUpsell : undefined }], {
          value: Number(productToRemove.grossPrice) * productToRemove.quantity,
        });
      }

      return {
        products: products.filter((p) => !isSamePurchaseCatalogLine(p, voucher)),
      };
    }),
  updateProductQuantity: (voucher, quantity) =>
    set((state) => {
      const { products, productsLimit } = state;
      const productToUpdate = products.find((p) => isSamePurchaseCatalogLine(p, voucher));

      if (!productToUpdate) return state;

      if (productsLimit !== undefined && quantity > productToUpdate.quantity) {
        const currentTotal = products.reduce((acc, p) => acc + p.quantity, 0);
        if (currentTotal - productToUpdate.quantity + quantity > productsLimit) {
          return state;
        }
      }

      if (quantity <= 0) {
        removeFromCartEvent([productToUpdate], {
          value: Number(productToUpdate.grossPrice) * productToUpdate.quantity,
        });
        return {
          products: products.filter((p) => !isSamePurchaseCatalogLine(p, voucher)),
        };
      }

      const updatedProducts = products.map((p) => (isSamePurchaseCatalogLine(p, voucher) ? { ...p, quantity } : p));

      if (quantity > productToUpdate.quantity) {
        addToCartEvent([{ ...productToUpdate, quantity: quantity - productToUpdate.quantity }], {
          value: Number(productToUpdate.grossPrice) * (quantity - productToUpdate.quantity),
        });
      } else if (quantity < productToUpdate.quantity) {
        removeFromCartEvent([{ ...productToUpdate, quantity: productToUpdate.quantity - quantity }], {
          value: Number(productToUpdate.grossPrice) * (productToUpdate.quantity - quantity),
        });
      }

      return { products: updatedProducts };
    }),
}));
